/**
 * User role enumeration for role-based access control
 * Requirements: 1.1, 1.2 - Authentication and role-based access
 */
export enum UserRole {
  EMPLOYEE = 'Employee',
  MANAGER = 'Manager',
  FINANCE_ADMIN = 'Finance_Admin',
  HR_ADMIN = 'HR_Admin',
  SUPER_ADMIN = 'Super_Admin'
}

/**
 * User interface representing system users
 * Requirements: 1.5 - User profile data storage
 */
export interface User {
  id: string;
  email: string;
  name: string;
  employeeId: string;
  department: string;
  /** Full Time / Intern / Consultant — see SimpleUser.employmentType for storage details. */
  employmentType?: string;
  /** Team / product line, one of TEAM_OPTIONS — see SimpleUser.team for why it is not `department`. */
  team?: string;
  /** Date of birth (ISO). UserProfiles-backed, never a Cognito attribute. */
  dateOfBirth?: string;
  /**
   * The employee's code on the BIOMETRIC DEVICE, when it differs from `employeeId`.
   *
   * Team Office numbers employees separately from Employee Master, so somebody whose HR code is
   * "EMP0001" can be "0020" on the reader. The attendance sync matches a punch on this first and
   * falls back to `employeeId`; without it, a punch from a differently-numbered device matches
   * nobody and the employee shows as Absent. UserProfiles-backed, like dateOfBirth above.
   */
  deviceCode?: string;
  /**
   * Mobile number in E.164 ("+919876543210"), for the attendance shortfall SMS.
   *
   * UserProfiles-backed like `deviceCode` above, and deliberately not Cognito's built-in
   * `phone_number` — that attribute carries verification and MFA meaning, and adopting it would
   * make a notification channel part of the sign-in path. Blank means no SMS for this employee;
   * the email notice is unaffected.
   */
  phone?: string;
  /**
   * Aadhaar (12 digits) and PAN (ABCDE1234F), for payroll.
   *
   * UserProfiles-backed, never Cognito attributes — same reason as dateOfBirth and deviceCode
   * above. Both optional: onboarding does not always finish before the account is needed.
   */
  aadhaarNumber?: string;
  panNumber?: string;
  managerEmail?: string;
  role: UserRole;
  shift?: string;
  primaryShift?: string;
  dateOfJoining?: string;
  dateOfExit?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * User creation input interface
 */
export interface CreateUserInput {
  email: string;
  name: string;
  employeeId: string;
  department: string;
  /** Required on creation; the Lambda rejects a create without one. */
  phone?: string;
  aadhaarNumber?: string;
  panNumber?: string;
  managerEmail?: string;
  role: UserRole;
  shift?: string;
  primaryShift?: string;
  dateOfJoining?: string;
  dateOfExit?: string;
}

/**
 * User update input interface
 */
export interface UpdateUserInput {
  name?: string;
  employeeId?: string;
  department?: string;
  phone?: string;
  aadhaarNumber?: string;
  panNumber?: string;
  managerEmail?: string;
  role?: UserRole;
  shift?: string;
  primaryShift?: string;
  dateOfJoining?: string;
  dateOfExit?: string;
}